import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="1988" className="hidden lg:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-none" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-primary font-bold text-[13px] tracking-wide transition-all duration-300 hover:text-secondary border-b-2 border-transparent hover:border-secondary py-1">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-none" />)}
      </div>;
  }
  return <nav className="flex flex-col p-6 gap-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-bold text-primary hover:text-secondary py-2 border-b border-border" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="mt-4">
        <Button className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold text-base py-6 rounded-none" asChild>
          <Link to="/ContactPage" onClick={onClose}>
            Schedule a Consultation
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="sticky top-0 z-50 w-full flex flex-col font-sans">
      {}
      <div className="h-[40px] bg-primary text-white flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-7xl flex justify-end items-center gap-6 text-[13px] font-bold">
          <Link to="/ServicesAndSEOPage" className="hover:text-secondary transition-colors hidden sm:block">
            Business Growth Solutions
          </Link>
          <Link to="tel:8005550199" className="flex items-center gap-2 hover:text-secondary transition-colors">
            <Phone className="h-3.5 w-3.5 text-secondary" />
            <span>(708) 374-4434</span>
          </Link>
        </div>
      </div>

      {}
      <div className={`h-[80px] w-full transition-all duration-300 border-b border-border/50 ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-md" : "bg-background"}`}>
        <div className="w-full max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex-shrink-0">
            <img src="https://foxrivermarketing.net/wp-content/uploads/2026/03/FRMG-Logo-1.png" alt="Fox River Marketing Logo" className="object-contain !w-[100px] !h-[44px]" />
          </Link>

          {}
          <div className="hidden lg:block">
            <MenuProvider menu_id="27">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4">
            <Button className="hidden lg:inline-flex bg-secondary hover:bg-secondary/90 text-white font-bold rounded-none px-6 py-5 shadow-md transition-transform hover:scale-105" asChild>
              <Link to="/ContactPage">Schedule a Consultation</Link>
            </Button>

            {}
            <Button variant="ghost" size="icon" className="lg:hidden text-primary hover:text-secondary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-[120px] left-0 w-full bg-background border-b border-border shadow-xl animate-in slide-in-from-top-5 duration-200 z-40">
          <MenuProvider menu_id="27">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}